/**
 * Trailing debounce that exposes `flush()` and `cancel()`.
 *
 * - `debounced(...args)` schedules `fn` to run after `wait` ms with the most
 *   recent args, replacing any pending call.
 * - `flush()` runs the pending call immediately (if any) and clears the timer.
 *   Used on blur / submit so the latest typed value reaches downstream
 *   consumers before a user-driven action like a button click.
 * - `cancel()` clears the timer and discards pending args without invoking fn.
 *
 * Extracted from `components/search-form/location/AddressAutosuggest.jsx`
 * so it can be unit-tested in isolation and reused by future autosuggest
 * variants that need flush-on-blur behavior.
 */
const debounceWithFlush = (wait, fn) => {
  let timeoutId = null;
  let pendingArgs = null;

  const debounced = (...args) => {
    pendingArgs = args;
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      timeoutId = null;
      const args2 = pendingArgs;
      pendingArgs = null;
      fn(...args2);
    }, wait);
  };

  debounced.flush = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
      const args2 = pendingArgs;
      pendingArgs = null;
      if (args2) fn(...args2);
    }
  };

  debounced.cancel = () => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = null;
    pendingArgs = null;
  };

  return debounced;
};

export default debounceWithFlush;
